import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Invoice from '../components/Invoice';
import { 
  generateInvoiceData, 
  generateWhatsAppURL, 
  validateCartForInvoice,
  applyCoupon 
} from '../utils/invoiceGenerator';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, clearCart, getCartSavings } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  
  // Calculate cart totals
  const subtotal = cartItems.reduce((total, item) => total + (item.discount_price * item.quantity), 0);
  const shipping = subtotal > 1000 ? 0 : 100;
  const total = subtotal + shipping - discount;
  
  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(id, newQuantity);
    }
  };
  
  const handleRemoveItem = (id) => {
    removeFromCart(id);
    toast.success('Item removed from cart');
  };
  
  const handleApplyCoupon = () => {
    const discountAmount = applyCoupon(couponCode, subtotal);
    
    if (discountAmount > 0) {
      setDiscount(discountAmount);
      setCouponApplied(true);
      toast.success(`Coupon applied! You saved ₹${discountAmount.toFixed(2)}`);
    } else {
      toast.error('Invalid coupon code. Try: FIRST100, DIWALI15');
    }
  };

  const handleGenerateInvoice = () => {
    // Validate cart
    const validation = validateCartForInvoice(cartItems);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    try {
      // Generate invoice data
      const invoice = generateInvoiceData(cartItems, {
        customerInfo: customerInfo.name ? customerInfo : null,
        discount: discount,
        includeTax: false,
        couponCode: couponApplied ? couponCode : null
      });

      setInvoiceData(invoice);
      setShowInvoice(true);
      toast.success('Invoice generated successfully!');
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice. Please try again.');
    }
  };

  const handleSendToWhatsApp = () => {
    // Generate invoice if not already generated
    let invoice = invoiceData;
    if (!invoice) {
      const validation = validateCartForInvoice(cartItems);
      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      try {
        invoice = generateInvoiceData(cartItems, {
          customerInfo: customerInfo.name ? customerInfo : null,
          discount: discount,
          includeTax: false,
          couponCode: couponApplied ? couponCode : null
        });
        setInvoiceData(invoice);
      } catch (error) {
        console.error('Error generating invoice:', error);
        toast.error('Failed to generate invoice. Please try again.');
        return;
      }
    }

    try {
      const whatsappURL = generateWhatsAppURL(invoice);
      window.open(whatsappURL, '_blank');
      toast.success('Opening WhatsApp...');
      
      // Optionally clear cart after sending
      setTimeout(() => {
        if (window.confirm('Order sent! Would you like to clear your cart?')) {
          clearCart();
          setShowInvoice(false);
          setInvoiceData(null);
          navigate('/');
        }
      }, 1000);
    } catch (error) {
      console.error('Error sending to WhatsApp:', error);
      toast.error('Failed to open WhatsApp. Please try again.');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <h2 className="mt-4 text-2xl font-bold text-dental-800">Your cart is empty</h2>
        <p className="mt-2 text-gray-600">Looks like you haven't added any products to your cart yet.</p>
        <Link to="/products" className="mt-6 inline-block bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700">
          Browse Products
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Invoice Modal */}
      {showInvoice && invoiceData && (
        <Invoice 
          invoiceData={invoiceData} 
          onClose={() => setShowInvoice(false)} 
        />
      )}

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-dental-800 mb-8">Shopping Cart</h1>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Cart Items */}
        <div className="lg:w-2/3">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[60vh] lg:max-h-none overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Product
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Price
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Quantity
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cartItems.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                            <img
                              src={item.image_url || "https://via.placeholder.com/150?text=Dental+Product"}
                              alt={item.name}
                              className="h-full w-full object-cover object-center"
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-dental-800">{item.name}</div>
                            {item.category && (
                              <div className="text-xs text-gray-500">{item.category}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-dental-800">₹{item.discount_price.toFixed(2)}</div>
                        {item.discount_price < item.price && (
                          <div className="text-xs text-gray-500 line-through">₹{item.price.toFixed(2)}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="text-gray-500 focus:outline-none focus:text-dental-800 p-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                            className="mx-2 w-12 text-center border-gray-200 rounded-md focus:border-primary-500 focus:ring-primary-500"
                          />
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="text-gray-500 focus:outline-none focus:text-dental-800 p-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-dental-800">
                        ₹{(item.discount_price * item.quantity).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200">
              <Link to="/products" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Continue Shopping
                </span>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Order Summary */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-dental-800">Order Summary</h2>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-dental-800 font-medium">₹{subtotal.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="text-dental-800 font-medium">
                  {shipping > 0 ? `₹${shipping.toFixed(2)}` : 'Free'}
                </span>
              </div>
              
              {getCartSavings() > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Product Savings</span>
                  <span>₹{getCartSavings().toFixed(2)}</span>
                </div>
              )}

              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon Discount</span>
                  <span>-₹{discount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-4 flex justify-between">
                <span className="text-lg font-semibold text-dental-800">Total</span>
                <span className="text-lg font-semibold text-dental-800">₹{total.toFixed(2)}</span>
              </div>
              
              {/* Customer Info (Optional) */}
              <div className="pt-4 pb-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Customer Details (Optional)</h3>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {!couponApplied && (
                <div className="pt-4">
                  <label htmlFor="coupon" className="block text-sm font-medium text-gray-700 mb-1">
                    Apply Coupon Code
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-r-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
                    >
                      Apply
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Try: FIRST100 (10% off), DIWALI15 (15% off)
                  </p>
                </div>
              )}
              
              <div className="space-y-2 mt-4">
                <button
                  onClick={handleGenerateInvoice}
                  disabled={cartItems.length === 0}
                  className="w-full bg-gray-700 text-white py-3 px-6 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  <span>Generate Invoice</span>
                </button>

                <button
                  onClick={handleSendToWhatsApp}
                  disabled={cartItems.length === 0}
                  className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Send Order via WhatsApp</span>
                </button>
                
                <div className="mt-4 flex justify-center">
                  <img 
                    src="/barcode.jpeg" 
                    alt="Payment Barcode" 
                    className="max-w-xs w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Free shipping on orders over ₹1000
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                100% genuine products
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
                Easy returns within 7 days
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Cart;
